import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';

import { ActionTypes } from '@/components/Store.d';
import { Button } from '@/components/Button/Button';
import { config } from '@/lib/config';
import { Context } from '@/components/Store';
import { findValueByKey, getPricingText } from '@/lib/util';
import { Input } from '@/components/Input/Input';
import { Label } from '@/components/Label/Label';
import { languageStrings } from '@/l18n/de';
import { log } from '@/lib/log';
import { validateVoucher } from '@/lib/http';

type Props = {
  hidden?: boolean;
};

const VoucherInput = ({ hidden = false }: Props) => {
  const [state, dispatch] = useContext(Context);
  const [voucherCode, setVoucherCode] = useState('');
  const [validateAction, setValidateAction] = useState(false);
  const [showRedeemedDialog, setShowRedeemedDialog] = useState(true);
  const location = useLocation();
  const buttonRef = useRef<HTMLButtonElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (buttonRef.current && state.dialog.isHidden) {
      buttonRef.current.focus();
    }
  }, [state.dialog.isHidden]);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  useEffect(() => {
    if (!location.search?.length) {
      return;
    }
    const searchParams = new URLSearchParams(location.search);
    const voucher = searchParams.get('voucher');

    if (voucher?.length) {
      setVoucherCode(voucher);
      setShowRedeemedDialog(false);
      setValidateAction(true);
    }
  }, [location]);

  /**
   * Displays error message for invalid voucher code
   */
  const invalidVoucher = useCallback(() => {
    dispatch({
      type: ActionTypes.showDialog,
      payload: {
        title: languageStrings.dialogs.invalidVoucher.title,
        body: languageStrings.dialogs.invalidVoucher.body,
        actions: [
          {
            onClick: () => {
              dispatch({
                type: ActionTypes.enableForm,
                payload: {
                  key: 'disableOnSubmit',
                  value: false
                }
              });
              dispatch({ type: ActionTypes.hideDialog });
            }
          }
        ]
      }
    });
  }, [dispatch]);

  useEffect(() => {
    void (async () => {
      if (!voucherCode.length || !validateAction) {
        return;
      }

      if (voucherCode?.length >= config.voucher.minLength) {
        const voucherResponse = await validateVoucher(voucherCode);

        log(`Voucher code ${voucherCode}`, voucherResponse.isValid ? 'is valid' : 'is not valid');

        dispatch({
          type: ActionTypes.enableForm,
          payload: {
            key: 'disableOnSubmit',
            value: false
          }
        });

        if (voucherResponse.isValid) {
          dispatch({
            type: ActionTypes.setVoucher,
            payload: {
              ...voucherResponse,
              code: voucherCode
            }
          });

          const isFreePurchase = findValueByKey(voucherResponse?.offerInfo, 'paymentDetailsRequired') === false ? true : false;
          dispatch({
            type: ActionTypes.setFreePurchase,
            payload: isFreePurchase
          });

          dispatch({
            type: ActionTypes.setPricingText,
            payload: getPricingText(state.bundle, true, voucherResponse)
          });

          if (showRedeemedDialog) {
            dispatch({
              type: ActionTypes.showDialog,
              payload: {
                title: languageStrings.dialogs.validVoucher.title,
                body: languageStrings.dialogs.validVoucher.body(voucherCode),
                actions: [
                  {
                    onClick: () => {
                      dispatch({ type: ActionTypes.toggleVoucherInput, payload: false });
                      dispatch({ type: ActionTypes.hideDialog });
                    }
                  }
                ]
              }
            });
          }
        } else {
          invalidVoucher();
        }
      } else {
        invalidVoucher();
      }

      setValidateAction(false);
      setShowRedeemedDialog(true);
    })().catch(() => {
      // On error, show invalid voucher
      invalidVoucher();
      setValidateAction(false);
    });
  }, [validateAction, voucherCode, dispatch, invalidVoucher, showRedeemedDialog, state.bundle]);

  return hidden === false ? (
    <div className="voucher-input">
      <Input
        autoComplete={
          /* https://developer.mozilla.org/en-US/docs/Web/Security/Securing_your_site/Turning_off_form_autocompletion#Preventing_autofilling_with_autocompletenew-password */
          'new-password'
        }
        className="form--text"
        defaultValue={state.voucher?.code}
        id="voucherCode"
        minLength={config.voucher.minLength}
        name="voucherCode"
        onChange={(event) => setVoucherCode(event.target.value)}
        placeholder={languageStrings.forms.voucherCode.placeholder}
        ref={inputRef}
        type="text"
      />
      <Label
        htmlFor="voucherCode"
        required={false}
        srOnly={true}
        text={languageStrings.forms.voucherCode.label}
      />
      <Button
        ariaLabel={languageStrings.buttons.redeemVoucher}
        className={`form--button-cta form--button-small ${state.disableOnSubmit ? 'loading' : ''}`.trim()}
        onClick={() => setValidateAction(true)}
        ref={buttonRef}
        type="button"
      >
        {languageStrings.buttons.redeemVoucher}
      </Button>
    </div>
  ) : null;
};

export { VoucherInput };
